import { Close as CloseIcon } from "@mui/icons-material";
import {
    AppBar,
    Dialog,
    DialogTitle,
    IconButton,
    Step,
    StepLabel,
    Stepper,
    Toolbar,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useReducer } from "react";

import { FullScreenDialogTransition } from "@/base";
import { useSelectedEventStateContext } from "@/contexts";

import { emergencyImportDialogStateMachine } from "./emergency-import-dialog-state-machine";
import { EmergencyImportDialogFileUploadStepContent } from "./emergency-import-dialog__file-upload-step-content";
import { EmergencyImportDialogImportStepContent } from "./emergency-import-dialog__import-step-content";
import { EmergencyImportDialogModeSelectionStepContent } from "./emergency-import-dialog__mode-selection-step-content";
import { EmergencyImportDialogReviewStepContent } from "./emergency-import-dialog__review-step-content";

const { Step: DialogStep, Action, initialState, reducer, convertStepToDisplayStep } = emergencyImportDialogStateMachine;

const EmergencyImportDialog = ({ close, isOpen }) => {
    const theme = useTheme();
    const isFullScreen = useMediaQuery(theme.breakpoints.down("sm"));

    const queryClient = useQueryClient();
    const [selectedEvent] = useSelectedEventStateContext();

    const [state, dispatchState] = useReducer(reducer, initialState);

    const displayStep = convertStepToDisplayStep(state.step);

    const handleModeSelectionStepContentNext = (formData) => {
        dispatchState({ type: Action.NEXT, formData });
    };

    const handleFileUploadStepContentNext = (formData) => {
        dispatchState({ type: Action.NEXT, formData });
    };

    const handleFileUploadStepContentPrevious = () => {
        dispatchState({ type: Action.PREVIOUS });
    };

    const handleImportStepContentNext = (formData) => {
        dispatchState({ type: Action.NEXT, formData });
    };

    const handleImportStepContentPrevious = () => {
        dispatchState({ type: Action.PREVIOUS });
    };

    const handleReviewStepContentClose = () => {
        queryClient.invalidateQueries({ queryKey: ["results", selectedEvent.id] });
        close();
    };

    const handleClosingButtonClick = () => {
        if (state.step === DialogStep.REVIEW) {
            handleReviewStepContentClose();
        } else {
            close();
        }
    };

    return (
        <Dialog
            open={isOpen}
            onClose={handleClosingButtonClick}
            fullScreen={isFullScreen}
            maxWidth="sm"
            fullWidth
            TransitionComponent={FullScreenDialogTransition}
        >
            {isFullScreen && (
                <AppBar position="relative">
                    <Toolbar>
                        <IconButton edge="start" color="inherit" onClick={handleClosingButtonClick}>
                            <CloseIcon />
                        </IconButton>
                        <Typography variant="h6" sx={{ ml: 2, flex: 1 }}>
                            Notfall-Import
                        </Typography>
                    </Toolbar>
                </AppBar>
            )}
            {!isFullScreen && (
                <DialogTitle>
                    Notfall-Import
                    <IconButton
                        onClick={handleClosingButtonClick}
                        sx={{
                            position: "absolute",
                            right: 8,
                            top: 8,
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
            )}
            <Stepper activeStep={displayStep} sx={{ px: 3, pt: 2 }}>
                <Step>
                    <StepLabel>Modus auswählen</StepLabel>
                </Step>
                <Step>
                    <StepLabel>Datei hochladen</StepLabel>
                </Step>
                <Step>
                    <StepLabel>Importieren</StepLabel>
                </Step>
                <Step>
                    <StepLabel>Abgeschlossen</StepLabel>
                </Step>
            </Stepper>
            {state.step === DialogStep.MODE_SELECTION && (
                <EmergencyImportDialogModeSelectionStepContent
                    defaultFormData={state.formDatas.modeSelectionFormData}
                    onNext={handleModeSelectionStepContentNext}
                />
            )}
            {state.step === DialogStep.FILE_UPLOAD && (
                <EmergencyImportDialogFileUploadStepContent
                    onNext={handleFileUploadStepContentNext}
                    onPrevious={handleFileUploadStepContentPrevious}
                />
            )}
            {state.step === DialogStep.IMPORT && (
                <EmergencyImportDialogImportStepContent
                    modeSelectionFormData={state.formDatas.modeSelectionFormData}
                    fileUploadFormData={state.formDatas.fileUploadFormData}
                    onNext={handleImportStepContentNext}
                    onPrevious={handleImportStepContentPrevious}
                />
            )}
            {state.step === DialogStep.REVIEW && (
                <EmergencyImportDialogReviewStepContent
                    importData={state.formDatas.importFormData}
                    onClose={handleReviewStepContentClose}
                />
            )}
        </Dialog>
    );
};

export { EmergencyImportDialog };